import { fetchBlockRequest } from "actions";
import { BBABalance, BlockNumber } from "components/blockchain";
import { BlockHash } from "components/common/BlockHash";
import moment from "moment";
import { useEffect } from "react";
import { HiOutlineLightBulb } from "react-icons/hi";
import { PiQuestionLight } from "react-icons/pi";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { RootState } from "reducers";
import { AppDispatch } from "store";
import { formatNumber } from "utils";
import Transactions from "./Transactions";

const BlockDetails = () => {
  const { height } = useParams();
  const blockHeight = parseInt(height) || 0;

  const dispatch = useDispatch<AppDispatch>();
  const { block } = useSelector((state: RootState) => state.block);

  useEffect(() => {
    dispatch(fetchBlockRequest(blockHeight));
  }, [dispatch, blockHeight]);

  if (!block) return null;

  return (
    <div className="mt-12 px-2.5 md:px-3 lg:px-5">
      <div className="box mb-4 overflow-hidden bg-white px-2.5 py-5 dark:bg-darkPrimary md:px-5">
        <div className="text-large border-b-[1px] border-gray-100 py-4 dark:border-lightGrey">
          Overview
        </div>
        <div className="mt-3 flex flex-col">
          <div className="grid grid-cols-2 gap-3 py-3 md:grid-cols-12">
            <div className="flex items-center md:col-span-3">
              <Link
                to="#"
                className="text-medium mr-2 text-gray-700 dark:text-gray-300"
                data-for="question-tip-block"
                data-tip="Unique sequential number identifying this block in the blockchain."
              >
                <PiQuestionLight />
              </Link>
              <span className="text-small text-gray-700 dark:text-gray-300">
                Block Height:
              </span>
            </div>
            <div className="md:col-span-9">
              <BlockNumber height={block.block_number} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 py-3 md:grid-cols-12">
            <div className="flex items-center md:col-span-3">
              <Link
                to="#"
                className="text-medium mr-2 text-gray-700 dark:text-gray-300"
                data-for="question-tip-block"
                data-tip="Unique identifier hash for this block."
              >
                <PiQuestionLight />
              </Link>
              <span className="text-small text-gray-700 dark:text-gray-300">
                Block Hash:
              </span>
            </div>
            <div className="md:col-span-9">
              <div className="flex items-center gap-[2px]">
                <BlockHash
                  hash={block.block_hash}
                  height={block.block_number}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 py-3 md:grid-cols-12">
            <div className="flex items-center md:col-span-3">
              <Link
                to="#"
                className="text-medium mr-2 text-gray-700 dark:text-gray-300"
                data-for="question-tip-block"
                data-tip="Each block rewards the producer with a limited amount of BBA, in addition to transaction fees from that block."
              >
                <PiQuestionLight />
              </Link>
              <span className="text-small text-gray-700 dark:text-gray-300">
                Block Reward:
              </span>
            </div>
            <div className="md:col-span-9">
              <BBABalance
                daltons={block.rewards.reduce(
                  (acc: any, item: any) => acc + item.daltons,
                  0
                )}
              />
              {/* <span className="mr-2"></span> */}
              {/* <span className="text-gray-700">($0.00056)</span> */}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 py-3 md:grid-cols-12">
            <div className="flex items-center md:col-span-3">
              <Link
                to="#"
                className="text-medium mr-2 text-gray-700 dark:text-gray-300"
                data-for="question-tip-block"
                data-tip="A period indicating block count and affecting rewards."
              >
                <PiQuestionLight />
              </Link>
              <span className="text-small text-gray-700 dark:text-gray-300">
                Epoch:
              </span>
            </div>
            <div className="md:col-span-9">
              <span className="text-green-900">{block.epoch}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 py-3 md:grid-cols-12">
            <div className="flex items-center md:col-span-3">
              <Link
                to="#"
                className="text-medium mr-2 text-gray-700 dark:text-gray-300"
                data-for="question-tip-block"
                data-tip="Number of transactions included in this block."
              >
                <PiQuestionLight />
              </Link>
              <span className="text-small text-gray-700 dark:text-gray-300">
                Transactions:
              </span>
            </div>
            <div className="md:col-span-9">
              <span className="text-green-900">
                {formatNumber(block.transactions.length || 0, 0)} transactions
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 border-b-[1px] border-gray-100 py-3 dark:border-lightGrey md:grid-cols-12">
            <div className="flex items-center md:col-span-3">
              <Link
                to="#"
                className="text-medium mr-2 text-gray-700 dark:text-gray-300"
                data-for="question-tip-block"
                data-tip="The moment when a this block was produces and added to the chain."
              >
                <PiQuestionLight />
              </Link>
              <span className="text-small text-gray-700 dark:text-gray-300">
                Timestamp:
              </span>
            </div>
            <div className="md:col-span-9">
              <span>
                {moment.unix(block.block_time).fromNow()} (
                {moment
                  .unix(block.block_time)
                  .format("MMM DD, YYYY hh:mm:ss A")}
                )
              </span>
            </div>
          </div>
          {/* <div className="grid grid-cols-2 gap-3 py-3 md:grid-cols-12">
                <div className="flex items-center md:col-span-3">
                  <Link
                    to="#"
                    className="text-medium mr-2 text-gray-700 dark:text-gray-300"
                    data-for="question-tip-block"
                    data-tip="Address recieving fees from transaction in this block"
                  >
                    <PiQuestionLight />
                  </Link>
                  <span className="text-small text-gray-700 dark:text-gray-300">
                    Fee Recipient:
                  </span>
                </div>
                <div className="flex items-center md:col-span-9">
                  <span className="mr-2 text-green-900">Binance2</span>
                  <button className="text-gray-700 dark:text-gray-300">
                    <IoIosCopy />
                  </button>
                </div>
              </div> */}

          {/* <div className="grid grid-cols-2 gap-3 py-3 md:grid-cols-12">
                <div className="flex items-center md:col-span-3">
                  <Link
                    to="#"
                    className="text-medium mr-2 text-gray-700 dark:text-gray-300"
                    data-for="question-tip-block"
                    data-tip="Total difficulty of the chain until this block."
                  >
                    <PiQuestionLight />
                  </Link>
                  <span className="text-small text-gray-700 dark:text-gray-300">
                    Total Difficulty:
                  </span>
                </div>
                <div className="md:col-span-9">
                  <span className="">0</span>
                </div>
              </div> */}
          <div className="grid grid-cols-2 gap-3 py-3 md:grid-cols-12">
            <div className="flex items-center md:col-span-3">
              <Link
                to="#"
                className="text-medium mr-2 text-gray-700 dark:text-gray-300"
                data-for="question-tip-block"
                data-tip="Unique identifier hash for previous block."
              >
                <PiQuestionLight />
              </Link>
              <span className="text-small text-gray-700 dark:text-gray-300">
                Previous Blockhash:
              </span>
            </div>
            <div className="md:col-span-9">
              {block.block_number - 1 > 0 && (
                <BlockHash
                  hash={block.prev_block_hash}
                  height={block.block_number - 1}
                  link
                />
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 border-b-[1px] border-gray-100 py-3 dark:border-lightGrey md:grid-cols-12">
            <div className="flex items-center md:col-span-3">
              <Link
                to="#"
                className="text-medium mr-2 text-gray-700 dark:text-gray-300"
                data-for="question-tip-block"
                data-tip="Unique sequential number identifying previous block in the blockchain."
              >
                <PiQuestionLight />
              </Link>
              <span className="text-small text-gray-700 dark:text-gray-300">
                Previous Block:
              </span>
            </div>
            <div className="md:col-span-9">
              {block.block_number - 1 > 0 && (
                <span className="">{formatNumber(block.block_number - 1)}</span>
              )}
            </div>
          </div>
          {/* <div className="grid grid-cols-2 gap-3 py-3 md:grid-cols-12">
                <div className="flex items-center md:col-span-3">
                  <Link
                    to="#"
                    className="text-medium mr-2 text-gray-700 dark:text-gray-300"
                    data-for="question-tip-block"
                    data-tip="The total gas used in the block and its percentage of gas filled in the block."
                  >
                    <PiQuestionLight />
                  </Link>
                  <span className="text-small text-gray-700 dark:text-gray-300">
                    Gas Used:
                  </span>
                </div>
                <div className="md:col-span-9">
                  <span className="">11,856,578</span>
                  <span className="text-gray-700">(36.66%)</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 border-b-[1px] border-gray-100 py-3 dark:border-lightGrey md:grid-cols-12">
                <div className="flex items-center md:col-span-3">
                  <Link
                    to="#"
                    className="text-medium mr-2 text-gray-700 dark:text-gray-300"
                    data-for="question-tip-block"
                    data-tip="Total gas limit provided by all transactions in the block"
                  >
                    <PiQuestionLight />
                  </Link>
                  <span className="text-small text-gray-700 dark:text-gray-300">
                    Gas Limit:
                  </span>
                </div>
                <div className="md:col-span-9">
                  <span className="">30,000,000</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 border-b-[1px] border-gray-100 py-3 dark:border-lightGrey md:grid-cols-12">
                <div className="flex items-center md:col-span-3">
                  <Link
                    to="#"
                    className="text-medium mr-2 text-gray-700 dark:text-gray-300"
                    data-for="question-tip-block"
                    data-tip="The least amount of gas required for a transaction to be added to a block."
                  >
                    <PiQuestionLight />
                  </Link>
                  <span className="text-small text-gray-700 dark:text-gray-300">
                    Base Fee Per Gas:
                  </span>
                </div>
                <div className="md:col-span-9">
                  <span className="">0.00002 BBA</span>
                </div>
              </div> */}
        </div>
      </div>

      {/* Transactions Section */}
      <div className="box mb-4 overflow-hidden bg-white px-2.5 py-5 dark:bg-darkPrimary md:px-5">
        <Transactions transactions={block.transactions} />
      </div>

      <div className="text-xsmall mb-10 flex items-center text-gray-700 dark:text-gray-300">
        <span className="text-large mb-1 mr-1">
          <HiOutlineLightBulb />
        </span>
        <span>
          Blocks consist of groups of transactions that are interconnected
          through cryptographic hashes. If a block is altered, it would render
          all subsequent blocks invalid since the hashes that follow would be
          affected. Discover more about this topic in our
          <span className="ml-1 text-green-900">Knowledge Hub</span>.
        </span>
      </div>
    </div>
  );
};

export default BlockDetails;
